import { Box, useTheme } from '@mui/material';
import { Button, Flex, Input, SlidingPanel } from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import { NomineeCustomer } from '@features/api/response/NomineeCustomer';
import {
  useCreateInvestmentAttributionMutation,
  useUpdateInvestmentAttributionMutation,
} from '@features/api/nomineeInvestmentsSlice';
import { useForm } from 'react-hook-form';
import { NewNomineeInvestmentAttribution } from '@features/api/request/NomineeInvestmentAttribution';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { NomineeInvestmentAttributionFormScema } from '@validationSchemas/nominee';
import { NomineeInvestmentAttribution } from '@features/api/response/NomineeInvestment';
import { useEffect } from 'react';

export interface NewInvestmentPanelProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: NomineeCustomer;
  investmentAttribution?: Pick<
    NomineeInvestmentAttribution,
    'id' | 'nomineeCustomerId' | 'transactionId'
  >;
}

export const NewInvestmentPanel = ({
  isOpen,
  onClose,
  customer,
  investmentAttribution,
}: NewInvestmentPanelProps) => {
  const theme = useTheme();
  const intl = useIntl();

  const [createMutation] = useCreateInvestmentAttributionMutation();
  const [updateMutation] = useUpdateInvestmentAttributionMutation();

  const {
    register,
    reset,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<NewNomineeInvestmentAttribution>({
    resolver: yupResolver(NomineeInvestmentAttributionFormScema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      nomineeCustomerId:
        customer ?? investmentAttribution
          ? customer?.id ?? investmentAttribution?.nomineeCustomerId
          : undefined,
      transactionId: investmentAttribution
        ? investmentAttribution.transactionId
        : undefined,
    },
  });

  useEffect(() => {
    reset({
      nomineeCustomerId:
        customer ?? investmentAttribution
          ? customer?.id ?? investmentAttribution?.nomineeCustomerId
          : undefined,
      transactionId: investmentAttribution
        ? investmentAttribution.transactionId
        : undefined,
    });
  }, [investmentAttribution, customer, reset]);

  const handleSave = async (data: NewNomineeInvestmentAttribution) => {
    if (!investmentAttribution) {
      await createMutation({
        amount: data.amount,
        nomineeCustomerId: data.nomineeCustomerId,
        transactionId: data.transactionId,
      }).unwrap();
    } else {
      await updateMutation({
        amount: data.amount,
        investmentAttributionId: investmentAttribution.id,
      });
    }

    onClose();
    reset();
  };

  return (
    <SlidingPanel
      anchor='right'
      open={isOpen}
      onClose={onClose}
      title={intl.formatMessage({
        id: !investmentAttribution
          ? 'crm.investments.newInvestment'
          : 'crm.investments.editInvestment',
      })}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      PaperProps={{
        sx: {
          padding: theme.spacing(14),
          borderRadius: 3,
          width: { xs: '100%', md: '40%' },
        },
      }}
    >
      <Flex
        flex={1}
        flexDirection='column'
        justifyContent='space-between'
        mt={14}
      >
        <form>
          <Flex flexWrap='wrap' gap={6}>
            <Input
              label='Amount'
              {...registerInput(register, 'amount')}
              {...getErrorProps(intl, errors.amount)}
              sx={{
                flex: '0 49%',
              }}
            />
            <Input
              label='Transaction ID'
              disabled={!!investmentAttribution}
              {...registerInput(register, 'transactionId')}
              {...getErrorProps(intl, errors.transactionId)}
              sx={{
                flex: '0 49%',
              }}
            />
            <Input
              label='Customer ID'
              disabled={!!customer || !!investmentAttribution}
              {...registerInput(register, 'nomineeCustomerId')}
              {...getErrorProps(intl, errors.nomineeCustomerId)}
              sx={{
                flex: '0 49%',
              }}
            />
          </Flex>
        </form>
        <Box mt={14}>
          <Button
            data-testid='save-button'
            variant='primary'
            type='submit'
            onClick={handleSubmit(handleSave)}
            disabled={!isValid}
          >
            <FormattedMessage id='misc.save' />
          </Button>
        </Box>
      </Flex>
    </SlidingPanel>
  );
};
